import { mdiAccount, mdiCakeVariant, mdiPhone } from '@mdi/js';
import { Fade } from '@mui/material';
import copy from 'copy-text-to-clipboard';
import { format } from 'date-fns';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import {
    Address,
    Invoice,
    ProviderGuarantor,
    ProviderInstitutionalClaimTemplate,
    ProviderPatient,
    ProviderPayerSubscriber,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    RelationshipType,
    SexType,
    SupportGuarantor,
    SupportInstitutionalClaimTemplate,
    SupportPatient,
    SupportPayerSubscriber,
    SupportSubscriber,
    SupportSubscriberPatient
} from '../../../api';
import { ToastContext } from '../../../services';
import { SubscriberBadge } from '../../badge';
import { Emoji, LoadingMeter, Spinner } from '../../base';
import { renderAmount } from '../../data-table';
import { ActionItem, CardAction, formatPhoneNumber } from '../cards/cards';
import * as S from './personal-overview.styles';

export interface IPersonalOverviewProps {
    loading: boolean;
    guarantor?: ProviderGuarantor | SupportGuarantor;
    subscriberPatient?: ProviderSubscriberPatient | SupportSubscriberPatient;
    payerSubscriber?: ProviderPayerSubscriber | SupportPayerSubscriber;
    institutionalTemplate?: ProviderInstitutionalClaimTemplate | SupportInstitutionalClaimTemplate;

    onEditPatient?: (patient: SupportPatient | ProviderPatient) => void;
    onEditSubscriber?: (subscriber: SupportSubscriber | ProviderSubscriber) => void;
    onEditGuarantor?: (guarantor: SupportGuarantor | ProviderGuarantor) => void;

    onGuarantorCheckout: () => Promise<void>;
    onGuarantorLink: () => Promise<void>;

    patientInvoice?: Invoice;

    guarantorActions?: CardAction[];
    accountId?: string;
}

type TabType = 'patient' | 'subscriber' | 'guarantor';

export const PersonalOverview: React.FC<IPersonalOverviewProps> = (props: IPersonalOverviewProps) => {
    const {
        loading,
        guarantor,
        subscriberPatient,
        payerSubscriber,
        institutionalTemplate,
        patientInvoice,
        onEditPatient,
        onEditSubscriber,
        onEditGuarantor,
        onGuarantorCheckout,
        onGuarantorLink,
        guarantorActions,
        accountId,
    } = props;

    const toastContext = useContext(ToastContext);

    const patient = subscriberPatient?.patient;
    const subscriber = subscriberPatient?.subscriber;

    const [activeTab, setActiveTab] = useState<TabType>('guarantor');

    const switchTab = (type: TabType) => () => setActiveTab(type);

    const getGuarantorName = (guarantor : ProviderGuarantor | SupportGuarantor | undefined) => {
        if(guarantor?.organization && !guarantor?.last) {
            return guarantor?.organization;
        } else {
            return _([guarantor?.first, guarantor?.last]).compact().join(' ');
        }
    }

    const formatLocalDate = (dateStr: string, formatStr: string): string => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return format(date, formatStr);
    };

    const renderTabs = (): React.ReactElement => {
        const guarantorTab = guarantor ? (
            <S.Tab onClick={switchTab('guarantor')} active={activeTab === 'guarantor'} key="guarantor-tab">
                <S.TabName>{getGuarantorName(guarantor)}</S.TabName>
                <S.TabType>Guarantor</S.TabType>
            </S.Tab>
        ) : undefined;
        const patientTab = patient ? (
            <S.Tab onClick={switchTab('patient')} active={activeTab === 'patient'} key="patient-tab">
                <S.TabName>{_([patient.first, patient.last]).compact().join(' ')}</S.TabName>
                <S.TabType>Patient</S.TabType>
            </S.Tab>
        ) : undefined;
        // const subscriberTab = subscriber ? (
        //     <S.Tab onClick={switchTab('subscriber')} active={activeTab === 'subscriber'}>
        //         <S.TabName>{_([subscriber.first, subscriber.last]).compact().join(' ')}</S.TabName>
        //         <S.TabType>Subscriber</S.TabType>
        //     </S.Tab>
        // ) : undefined;

        const tabs = _.compact([guarantorTab, patientTab]);

        return <S.Tabs>{tabs}</S.Tabs>;
    };

    const renderLoader = (): React.ReactElement => {
        return (
            <Fade appear={true} in={true}>
                <S.Loader>
                    <LoadingMeter />
                </S.Loader>
            </Fade>
        );
    };

    const renderPatient = (): React.ReactElement | undefined => {
        if (subscriberPatient && patient) {
            const { id, dob, sex, addresses, nickname } = patient;

            return (
                <>
                    {!!nickname && (
                        <S.IconRow>
                            <Emoji type="cardIndex" title="Preferred Name" />
                            <S.RowLabel>Preferred Name: {nickname}</S.RowLabel>
                        </S.IconRow>
                    )}
                    {!!nickname && (
                        <S.IconRow>
                            <Emoji type="cardIndex" title="Clinical Name" />
                            <S.RowLabel>Legal Name: {_([patient.first, patient.last]).compact().join(' ')}</S.RowLabel>
                        </S.IconRow>
                    )}
                    {/* <S.IconRow>
                        <Emoji type="shield" />
                        <S.RowLabel>{id ? id : <S.EmptyLabel>Empty</S.EmptyLabel>}</S.RowLabel>
                    </S.IconRow> */}
                    <S.IconRow>
                        <Emoji type="cake" title="Date of birth" />
                        <S.RowLabel>{formatLocalDate(dob, 'MMMM dd, yyyy')}</S.RowLabel>
                    </S.IconRow>
                    <S.IconRow>
                        {renderSexIcon(sex)}
                        <S.RowLabel>{_.upperFirst(_.lowerCase(sex))}</S.RowLabel>
                    </S.IconRow>
                    <S.IconRow>
                        <Emoji type="houseGarden" title="Address" />
                        <S.RowLabel>{renderAddresses(addresses)}</S.RowLabel>
                    </S.IconRow>
                    {!!subscriberPatient.relationship && (
                        <S.IconRow>
                            {renderRelationshipIcon(subscriberPatient.relationship)}
                            <S.RowLabel>{_.upperFirst(_.lowerCase(subscriberPatient.relationship))}</S.RowLabel>
                        </S.IconRow>
                    )}

                </>
            );
        }
    };

    const renderSubscriber = (): React.ReactElement | undefined => {
        if (subscriber) {
            const { id, first, last, ssn, dob, addresses, sex, phone } = subscriber;

            const items = onEditSubscriber
                ? [{ action: () => onEditSubscriber(subscriber), label: 'Change Subscriber' }]
                : undefined;

            return (
                <>
                    <S.Row>
                        <S.Name>
                            {first} {last}
                        </S.Name>
                        <S.Spacer />
                        <SubscriberBadge
                            id={id}
                            first={first}
                            last={last}
                            tooltip="View claims for Subscriber"
                            path={`/claims/by-subscriber/${id}`}
                            items={items}
                        />
                    </S.Row>
                    <S.RecordRow>
                        <S.RecordLabel>SSN</S.RecordLabel>
                        <S.RecordNumber>{ssn}</S.RecordNumber>
                    </S.RecordRow>
                    <S.Spacer />
                    <S.IconRow>
                        <S.DobIcon path={mdiCakeVariant} />
                        {formatLocalDate(dob, 'MM/dd/yyyy')}
                    </S.IconRow>
                    <S.IconRow>
                        <S.PatientIcon path={mdiAccount} />
                        {sex}
                    </S.IconRow>
                    <S.IconRow>
                        <S.PhoneIcon path={mdiPhone} />
                        {phone ? phone : <S.EmptyLabel>Empty</S.EmptyLabel>}
                    </S.IconRow>
                    <S.IconRow>{renderAddresses(addresses)}</S.IconRow>
                </>
            );
        }
    };
    const renderGuarantor = (): React.ReactElement | undefined => {
        if (guarantor) {
            const { id, first, last, dob, addresses, email, phone, suffix, externalOrganizationId } = guarantor;

            const items = onEditGuarantor
                ? [{ action: () => onEditGuarantor(guarantor), label: 'Change Guarantor' }, ...(guarantorActions || [])]
                : undefined;

            return (
                <>
                    {/* <S.Row>
                        <S.Name>
                            {first} {last}
                        </S.Name>
                        <S.Spacer />
                        <GuarantorBadge id={id} first={first} last={last} suffix={suffix} items={items} />
                    </S.Row> */}
                    {/* <S.Spacer /> */}
                    {/* <S.IconRow>
                        <S.DobIcon path={mdiCakeVariant} />
                        {format(dob || '', 'MM/dd/yyyy')}
                    </S.IconRow> */}

                    {!!dob && (
                        <S.IconRow>
                            <Emoji type="cake" title="Date of birth" />
                            <S.RowLabel>{formatLocalDate(dob, 'MMMM dd, yyyy')}</S.RowLabel>
                        </S.IconRow>
                    )}
                    <S.IconRow>
                        <Emoji type="phoneReceiver" title="Phone" />
                        <S.RowLabel>{phone ? formatPhoneNumber(phone) : <S.EmptyLabel>Empty</S.EmptyLabel>}</S.RowLabel>
                    </S.IconRow>
                    <S.IconRow>
                        <Emoji type="email" title="Email" />
                        <S.RowLabel>{email ? email.toLowerCase() : <S.EmptyLabel>Empty</S.EmptyLabel>}</S.RowLabel>
                    </S.IconRow>
                    <S.IconRow>
                        <Emoji type="houseGarden" title="Address" />
                        <S.RowLabel>{renderAddresses(addresses)}</S.RowLabel>
                    </S.IconRow>
                    {!!externalOrganizationId && (
                        <S.IconRow>
                            <Emoji type="memo" title="External Org Id" />
                            <S.RowLabel>{externalOrganizationId}</S.RowLabel>
                        </S.IconRow>
                    )}
                    {/* <S.IconRow>{renderAddresses(addresses)}</S.IconRow> */}
                </>
            );
        }
    };

    const renderPatientActions = () => {
        if (!patient) {
            return [];
        }

        const path = `/claims/by-patient/${patient.id}`;
        const viewStatementPath = accountId 
            ? `/${accountId}/patients/${patient.id}/statement`
            : `/patients/${patient.id}/statement`;

        return renderActions(
            _.compact([
                onEditPatient
                    ? {
                        label: 'Edit Patient',
                        icon: <Emoji type="pencil" title="Edit Patient" />,
                        onClick: () => onEditPatient(patient),
                        divide: true,
                    }
                    : undefined,
                {
                    label: 'View statement',
                    icon: <Emoji type="receipt" title="View Statement" />,
                    href: viewStatementPath,
                },
            ]),
        );
    };

    const renderGuarantorActions = () => {
        if (!guarantor) {
            return [];
        }

        const viewStatementPath = patient 
            ? (accountId ? `/${accountId}/patients/${patient.id}/statement` : `/patients/${patient.id}/statement`)
            : undefined;

        const Actions = renderActions(
            _.compact([
                onEditGuarantor
                    ? {
                        label: 'Edit Guarantor',
                        icon: <Emoji type="pencil" title="Edit Guarantor" />,
                        onClick: () => onEditGuarantor(guarantor),
                        divide: true,
                    }
                    : undefined,
                ...(guarantorActions || []),
                {
                    label: 'View statement',
                    icon: <Emoji type="receipt" title="View Statement" />,
                    href: viewStatementPath,
                }
            ]),
        );

        return (
            <div>
                <GuarantorHeader
                    onGuarantorCheckout={onGuarantorCheckout}
                    onGuarantorLink={onGuarantorLink}
                    patientInvoice={patientInvoice}
                />
                {Actions}
            </div>
        );

        // return renderActions([
        //     {
        //         label: 'Payment Plan',
        //         icon: <Emoji type="alarmClock" />,
        //         action: () => {
        //             return;
        //         },
        //         divide: true,
        //     },
        //     {
        //         label: 'Refund',
        //         icon: <Emoji type="repeat" />,
        //         action: () => {
        //             return;
        //         },
        //     },
        //     {
        //         label: 'Record Payment',
        //         icon: <Emoji type="writingHand" />,
        //         action: () => {
        //             return;
        //         },
        //     },
        //     {
        //         label: 'Set Discount',
        //         icon: <Emoji type="moneyWings" />,
        //         action: () => {
        //             return;
        //         },
        //         divide: true,
        //     },
        //     {
        //         label: 'View Invoice',
        //         icon: <Emoji type="cardIndex" />,
        //         action: () => {
        //             return;
        //         },
        //     },
        // ]);
    };

    const renderActions = (actions: CardAction[]) => {
        return actions.map((action, i) => (
            <React.Fragment key={i}>
                <ActionItem action={action} />
                {!!action.divide && <S.Divider />}
            </React.Fragment>
        ));
    };

    const renderAddresses = (addresses?: Address[]): React.ReactElement | undefined => {
        if (addresses) {
            const address = _.first(addresses);
            if (!address) {
                return;
            }
            const parts: string[] = [];
            
            // Add street address lines
            if (address.lines && address.lines.length > 0) {
                parts.push(...address.lines);
            }
            
            // Add city, state, postal
            const cityStatePostal = _.compact([address.city, address.state, address.postal]).join(', ');
            if (cityStatePostal) {
                parts.push(cityStatePostal);
            }
            
            return <>{parts.join(', ')}</>;
        }
    };

    const renderSexIcon = (sex: SexType) => {
        const dict = {
            [SexType.MALE]: <Emoji type="male" title="Sex" />,
            [SexType.FEMALE]: <Emoji type="woman" title="Sex" />,
            [SexType.UNKNOWN]: <Emoji type="whiteQuestion" title="Sex" />,
        };

        return dict[sex];
    };

    const renderRelationshipIcon = (relationship: RelationshipType) => {
        const dict = {
            [RelationshipType.SPOUSE]: <Emoji type="coupleHeart" title="Relationship" />,
            [RelationshipType.SELF]: <Emoji type="astronaut" title="Relationship" />,
            [RelationshipType.CHILD]: <Emoji type="child" title="Relationship" />,
            [RelationshipType.EMPLOYEE]: <Emoji type="briefcase" title="Relationship" />,
            [RelationshipType.UNKNOWN]: <Emoji type="whiteQuestion" title="Relationship" />,
            [RelationshipType.ORGANDONOR]: <Emoji type="mendingHeart" title="Relationship" />,
            [RelationshipType.CADAVERDONOR]: <Emoji type="mendingHeart" title="Relationship" />,
            [RelationshipType.LIFEPARTNER]: <Emoji type="peopleHoldingHands" title="Relationship" />,
            [RelationshipType.OTHERRELATIONSHIP]: <Emoji type="peopleHoldingHands" title="Relationship" />,
        };

        return dict[relationship];
    };

    return (
        <S.PersonalOverviewCard size="large">
            <S.Container>
                <S.Header>{renderTabs()}</S.Header>
                <S.Body>
                    {loading && renderLoader()}
                    {!loading && activeTab === 'patient' && renderPatient()}
                    {!loading && activeTab === 'subscriber' && renderSubscriber()}
                    {!loading && activeTab === 'guarantor' && renderGuarantor()}
                </S.Body>
            </S.Container>
            {!loading && (
                <S.Actions>
                    {activeTab === 'patient' && renderPatientActions()}
                    {/* {!loading && activeTab === 'subscriber' && renderSubscriber()} */}
                    {activeTab === 'guarantor' && renderGuarantorActions()}
                </S.Actions>
            )}
        </S.PersonalOverviewCard>
    );
};

const GuarantorHeader = ({
                             onGuarantorCheckout,
                             onGuarantorLink,
                             patientInvoice,
                         }: {
    onGuarantorCheckout: () => Promise<void>;
    onGuarantorLink: () => Promise<void>;
    patientInvoice?: Invoice;
}) => {
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [linkLoading, setLinkLoading] = useState(false);

    const handleCheckout = async () => {
        setCheckoutLoading(true);

        try {
            await onGuarantorCheckout();
        } finally {
            setCheckoutLoading(false);
        }
    };

    const handleLink = async () => {
        setLinkLoading(true);

        try {
            await onGuarantorLink();
        } finally {
            setLinkLoading(false);
        }
    };

    return (
        <S.ActionsHeader>
            {patientInvoice && (
                <S.GuarantorAmount>
                    <S.PrimaryAmount>{renderAmount(patientInvoice.paidAmount)} / </S.PrimaryAmount>
                    <S.SecondaryAmount>{renderAmount(patientInvoice.amount)}</S.SecondaryAmount>
                </S.GuarantorAmount>
            )}
            {!patientInvoice && (
                <S.GuarantorAmount>
                    <S.PrimaryAmount>---- / </S.PrimaryAmount>
                    <S.SecondaryAmount>----</S.SecondaryAmount>
                </S.GuarantorAmount>
            )}
            <S.ActionsHeaderRow>
                <S.PayButton variant="primary" onClick={handleCheckout} disabled={checkoutLoading}>
                    {checkoutLoading ? (
                        <Spinner size="18px" />
                    ) : (
                        <>
                            <Emoji type="moneyBag" title="Pay bill" /> Pay
                        </>
                    )}
                </S.PayButton>
                <S.LinkButton variant="outlined" onClick={handleLink} disabled={linkLoading}>
                    {linkLoading ? (
                        <Spinner size="18px" />
                    ) : (
                        <>
                            <Emoji type="link" title="View bill" />
                        </>
                    )}
                </S.LinkButton>
            </S.ActionsHeaderRow>
        </S.ActionsHeader>
    );
};
